import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import crmKnowledgeBase, {
  type BaseConhecimentoItem,
  type Categoria,
  type Subcategoria,
} from '@/services/crmKnowledgeBase';

/**
 * Base de conhecimento do painel, consultada via CRM (local apenas por enquanto —
 * ver aviso de segurança em src/services/auth/crmAuthService.ts).
 *
 * Navegação por categoria -> subcategoria -> artigos, porque /suporte/base-conhecimento
 * sem filtro pode vir vazio: o padrão real do painel é listar artigos POR subcategoria
 * (rota dedicada .../por-subcategoria/:id).
 */
export default function BaseConhecimentoViewer() {
  const { crmToken } = useAuth();

  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [subcategoriasPorCategoria, setSubcategoriasPorCategoria] = useState<Record<string, Subcategoria[]>>({});
  const [categoriaAberta, setCategoriaAberta] = useState<string | null>(null);
  const [subcategoriaAtiva, setSubcategoriaAtiva] = useState<string | null>(null);

  const [itens, setItens] = useState<BaseConhecimentoItem[]>([]);
  const [busca, setBusca] = useState('');
  const [loadingSidebar, setLoadingSidebar] = useState(true);
  const [loadingItens, setLoadingItens] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const idDe = (obj: Categoria | Subcategoria) => String(obj.id ?? obj.idCategoria ?? obj.idSubCategoria ?? '');

  // Carrega a árvore de categorias assim que loga.
  useEffect(() => {
    if (!crmToken) {
      setLoadingSidebar(false);
      setErro('Faça login para consultar a base de conhecimento.');
      return;
    }
    let cancelado = false;
    setLoadingSidebar(true);
    setErro(null);
    crmKnowledgeBase
      .listarCategorias(crmToken)
      .then((lista) => {
        if (!cancelado) setCategorias(lista);
      })
      .catch((err) => {
        if (!cancelado) setErro(err instanceof Error ? err.message : 'Erro ao carregar categorias');
      })
      .finally(() => {
        if (!cancelado) setLoadingSidebar(false);
      });
    return () => {
      cancelado = true;
    };
  }, [crmToken]);

  // Sem seleção nenhuma: lista tudo / busca livre em /suporte/base-conhecimento.
  useEffect(() => {
    if (!crmToken || subcategoriaAtiva) return;
    let cancelado = false;
    setLoadingItens(true);
    crmKnowledgeBase
      .listar(crmToken, busca.trim() || undefined)
      .then((lista) => {
        if (!cancelado) setItens(lista);
      })
      .catch((err) => {
        if (!cancelado) setErro(err instanceof Error ? err.message : 'Erro ao carregar itens');
      })
      .finally(() => {
        if (!cancelado) setLoadingItens(false);
      });
    return () => {
      cancelado = true;
    };
  }, [crmToken, busca, subcategoriaAtiva]);

  const abrirCategoria = async (categoria: Categoria) => {
    const id = idDe(categoria);
    if (categoriaAberta === id) {
      setCategoriaAberta(null);
      return;
    }
    setCategoriaAberta(id);
    if (!crmToken || subcategoriasPorCategoria[id]) return;
    try {
      const lista = await crmKnowledgeBase.listarSubcategorias(id, crmToken);
      setSubcategoriasPorCategoria((prev) => ({ ...prev, [id]: lista }));
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao carregar subcategorias');
    }
  };

  const selecionarSubcategoria = async (subcategoria: Subcategoria) => {
    const id = idDe(subcategoria);
    setSubcategoriaAtiva(id);
    setBusca('');
    if (!crmToken) return;
    setLoadingItens(true);
    setErro(null);
    try {
      const lista = await crmKnowledgeBase.porSubcategoria(id, crmToken);
      setItens(lista);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao carregar artigos');
    } finally {
      setLoadingItens(false);
    }
  };

  const limparSelecao = () => {
    setSubcategoriaAtiva(null);
    setCategoriaAberta(null);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '1.5rem', padding: '1.5rem' }}>
      {/* Sidebar */}
      <aside style={{ borderRight: '1px solid #e2e8f0', paddingRight: '1rem' }}>
        <h3 style={{ marginBottom: '0.75rem', fontSize: 14, fontWeight: 700, color: '#334155' }}>
          Categorias
        </h3>

        <button
          type="button"
          onClick={limparSelecao}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '0.4rem 0.5rem',
            marginBottom: '0.5rem',
            borderRadius: 8,
            border: 'none',
            background: !subcategoriaAtiva ? '#ede9fe' : 'transparent',
            color: !subcategoriaAtiva ? '#6d28d9' : '#475569',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Todos os artigos
        </button>

        {loadingSidebar && <p style={{ fontSize: 13, color: '#94a3b8' }}>Carregando categorias...</p>}

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {categorias.map((categoria) => {
            const idCat = idDe(categoria);
            const aberta = categoriaAberta === idCat;
            const subs = subcategoriasPorCategoria[idCat] ?? [];
            return (
              <li key={idCat} style={{ marginBottom: 4 }}>
                <button
                  type="button"
                  onClick={() => abrirCategoria(categoria)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.4rem 0.5rem',
                    borderRadius: 8,
                    border: 'none',
                    background: 'transparent',
                    color: '#1e293b',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {aberta ? '▾' : '▸'} {categoria.nome || `Categoria #${idCat}`}
                </button>

                {aberta && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '2px 0 4px 1rem' }}>
                    {subs.length === 0 && (
                      <li style={{ fontSize: 12, color: '#94a3b8', padding: '0.2rem 0.5rem' }}>
                        Sem subcategorias
                      </li>
                    )}
                    {subs.map((sub) => {
                      const idSub = idDe(sub);
                      return (
                        <li key={idSub}>
                          <button
                            type="button"
                            onClick={() => selecionarSubcategoria(sub)}
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              padding: '0.3rem 0.5rem',
                              borderRadius: 6,
                              border: 'none',
                              background: subcategoriaAtiva === idSub ? '#ede9fe' : 'transparent',
                              color: subcategoriaAtiva === idSub ? '#6d28d9' : '#64748b',
                              fontSize: 12.5,
                              cursor: 'pointer',
                            }}
                          >
                            {sub.nome || `Subcategoria #${idSub}`}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Conteúdo */}
      <section>
        <h2 style={{ marginBottom: '1rem' }}>Base de Conhecimento (painel)</h2>

        {!subcategoriaAtiva && (
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por título ou descrição..."
            style={{
              width: '100%',
              maxWidth: 420,
              padding: '0.6rem 0.9rem',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              marginBottom: '1.25rem',
            }}
          />
        )}

        {loadingItens && <p>Carregando...</p>}
        {erro && <p style={{ color: '#dc2626' }}>{erro}</p>}

        {!loadingItens && !erro && itens.length === 0 && (
          <p style={{ color: '#64748b' }}>Nenhum item encontrado.</p>
        )}

        <ul style={{ display: 'grid', gap: '0.75rem', listStyle: 'none', padding: 0 }}>
          {itens.map((item) => (
            <li
              key={item.id}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: '1rem 1.25rem',
                background: '#fff',
              }}
            >
              <strong style={{ display: 'block', marginBottom: 4 }}>
                {item.titulo || item.nome || `Item #${item.id}`}
              </strong>
              {item.descricao && (
                <p style={{ margin: 0, color: '#475569', fontSize: 14 }}>{item.descricao}</p>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
